"use client";
import {
  marketingHomeUrl,
  DEFAULT_MARKETING_HOME,
  storefrontLocaleForHost,
} from "src/utils/marketing-home";
import {
  ColorModeContext,
  useOrder,
  useCartDrawer,
} from "@contexts";
import { useOrderQuery } from "src/hooks";
import { useArtCatalogueEnabled } from "src/utils/art-catalogue-enabled";
import { useGiftCardsEnabled } from "src/utils/gift-cards-enabled";
import { useBundleCards } from "src/hooks/useBundles";
import { useShopCurrency } from "@components/cashback/use-client-value";
import { useMapsEnabled } from "src/utils/maps-enabled";
import { guestTokenForActiveOrder } from "src/utils/guest-token-url-guard";
import { usePartyCameraEnabled } from "src/party/flag";
import { isMapDefinition, mapPageHref, showDefinitionInMenu } from "src/components/map/map-links";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { Link, usePathname } from "src/i18n/navigation";
import NextLink from "next/link";
import { useLocale } from "next-intl";
import { routing } from "src/i18n/routing";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { GetUserDto } from "src/services/generated-api";
import MuiLink from "@mui/material/Link";
import {
  REDIRECT_TO_ADMIN,
  REDIRECT_TO_HOME,
  REDIRECT_TO_LOGIN,
  REDIRECT_TO_ORDER_HISTORY,
  REDIRECT_TO_MY_EVENTS,
} from "src/constants/redirect.constant";
import { UserRole } from "src/enums/user";
import { Menu, Box } from "@mui/material";
import { authProvider } from "src/authProvider";
import nookies from "nookies";
import dynamic from "next/dynamic";
// Loaded once the page is interactive, not as part of it: the drawer stays
// closed until the customer opens the cart, and its code (every calendar
// design among it) was in the start-up JavaScript of every shop page.
const CartDrawer = dynamic(() => import("@components/cart-drawer"), {
  ssr: false,
});
import CashbackBanner from "@components/cashback/CashbackBanner";
import CreditReminderRibbon from "@components/credit-reminder/CreditReminderRibbon";
import { OrderStatus } from "src/enums/order";
import { useProductWithMediaDefinitions } from "src/hooks/api/useProductWithMedia";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { paidPhotoGroups } from "src/utils/order-photo-groups";
import { chromeSurface, chromeText, DARK_CHROME } from "src/themes/chrome-surfaces";
interface DefinitionItem {
  id: string;
  name?: string | Record<string, string>;
  active?: boolean;
  /** Drives which editor the menu links to — see definitionHref. */
  kind?: string;
}
interface HeaderProps {
  sticky?: boolean;
  onCartClick?: () => void;
  cartItemCount?: number;
}
export const Header: React.FC<HeaderProps> = ({
  sticky = true,
}) => {
  const { openCartDrawer } = useCartDrawer();
  const artCatalogueEnabled = useArtCatalogueEnabled();
  const giftCardsEnabled = useGiftCardsEnabled();
  const mapsEnabled = useMapsEnabled();
  const { mainOrderId, guestToken, activeOrderFinalized } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const orderContext = order;
  // Helper function to get total items count for badge (distinct items, not total quantity)
  const getTotalItemsCount = () => {
    // A finalized order (paid / COD placed / shipped / ...) is no longer the
    // cart, so the badge reads empty even though its id is still the active
    // order for the order-view page (e.g. /summary) that set it.
    if (activeOrderFinalized) return 0;
    // Count number of photo groups (orderPhotos) that actually hold photos.
    // Counting every row made the badge disagree with the drawer, which needs
    // at least one real photo before it shows anything: a failed upload leaves
    // an empty group behind, so the badge said "2" and the drawer said the cart
    // was empty. Same predicate on both sides now.
    // The free enlargement is not counted: it comes with the basket.
    const photoGroupsCount = paidPhotoGroups(order?.orderPhotos).length;
    // Count number of products with quantity > 0 (orderProducts)
    const productTypesCount =
      order?.orderProductDetails?.reduce((count, orderProductDetail) => {
        return count + orderProductDetail.quantity;
      }, 0) || 0;
    // Count number of calendars
    const calendarsCount = order?.orderCalendars?.length || 0;
    // Count number of product-with-media items (by quantity)
    const productWithMediaCount =
      order?.productWithMediaOrders?.reduce((c, l) => c + (l.quantity || 1), 0) ||
      0;
    return (
      photoGroupsCount + productTypesCount + calendarsCount + productWithMediaCount
    );
  };
  // Handle opening the cart drawer
  const handleOpenDrawer = async () => {
    openCartDrawer();
  };
  // Where the logo sends the customer: decided by the storefront they are ON.
  //
  // This was previously derived from the `referer` prop, and no call site ever
  // passed one — so the default ("https://fotodelavnica.si") won every time and
  // every non-Slovenian storefront sent its customers to the Slovenian site.
  // The referer branching was unreachable code.
  //
  // Resolved after mount rather than during render: the server has no window,
  // and rendering a host-derived href on the server would mismatch on hydration.
  // The first paint uses the same default the server produced, then corrects.
  const [homeLink, setHomeLink] = useState(DEFAULT_MARKETING_HOME);
  useEffect(() => {
    setHomeLink(marketingHomeUrl(window.location.hostname));
  }, []);
  const { mode, setMode } = useContext(ColorModeContext);
  const pathname = usePathname();
  const currentLocale = useLocale();
  // Linked only while the switch is on AND something is offered: an empty
  // Paketi page is a dead end. useBundleCards is gated by useBundlesEnabled.
  const shopCurrency = useShopCurrency();
  const { enabled: partyEnabled } = usePartyCameraEnabled();
  const { data: bundleCards } = useBundleCards(shopCurrency, currentLocale);
  const bundlesOffered = (bundleCards?.length ?? 0) > 0;
  const locales = [...routing.locales];
  const isOnAdminPage = pathname?.includes("/admin") ?? false;
  const [user, setUser] = useState<GetUserDto | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const [produktiAnchorEl, setProduktiAnchorEl] = useState<null | HTMLElement>(null);
  const showUserSettings = Boolean(anchorEl);
  const produktiOpen = Boolean(produktiAnchorEl);
  // Hover-open dropdown for "Produkti": open on enter, close on leave with a
  // small grace delay so the cursor can travel into the menu without it closing.
  const produktiCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openProdukti = (el: HTMLElement) => {
    if (produktiCloseTimer.current) clearTimeout(produktiCloseTimer.current);
    setProduktiAnchorEl(el);
  };
  const scheduleCloseProdukti = () => {
    if (produktiCloseTimer.current) clearTimeout(produktiCloseTimer.current);
    produktiCloseTimer.current = setTimeout(() => setProduktiAnchorEl(null), 140);
  };
  const { data: definitionsData } = useProductWithMediaDefinitions();
  const definitions = ((definitionsData?.data ?? []) as unknown as DefinitionItem[]);
  // Hide admin-deactivated definitions (active === false) from the public
  // Produkti dropdown; a missing/true flag stays visible.
  const activeDefinitions = definitions.filter(
    (def) => def.active !== false && showDefinitionInMenu(def, { mapsEnabled }),
  );
  const getDefName = (def: DefinitionItem): string => {
    const raw = def.name;
    if (!raw) return "Custom design";
    if (typeof raw === "string") return raw;
    return (raw as Record<string, string>)[currentLocale]
      ?? (raw as Record<string, string>).sl
      ?? (raw as Record<string, string>).en
      ?? Object.values(raw as Record<string, string>)[0]
      ?? "Custom design";
  };
  const isAdmin =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERADMIN;
  const currentPath = pathname ?? "/";
  const translate = useAppTranslate();
  useEffect(() => {
    if (!nookies.get(null).bearerToken) return;
    import("src/services/api").then(({ userApi }) => {
      userApi.userControllerGetMe().then(res => setUser(res.data)).catch(() => {});
    });
  }, []);
  const handleClickUserSettings = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };
  const showHome = true;
  const isHome = currentPath === "/" || locales.some(locale => currentPath === `/${locale}` || currentPath === `/${locale}/`);
  const isCalendarPage = currentPath.includes("/calendar");
  const isDesignPage = currentPath.includes("/design/"); // greeting-card / product-with-media editor
  // Matches both /canvas/<id> and the id-less public entry route /canvas.
  // `.includes("/canvas/")` missed the latter, hiding the cart on it.
  const isCanvasPage = /\/canvas(\/|$)/.test(currentPath); // large-format editor
  // The ready-made catalogue: hub, both galleries and every piece page.
  const isCataloguePage = /\/(wall-art|art|photography)(\/|$)/.test(currentPath);
  const isMapPage = /\/zemljevid(\/|$)/.test(currentPath); // map print editor

  // product-with-media covers greeting cards AND canvas, which have completely
  // different editors. Route by kind rather than sending everything to /design.
  const definitionHref = (def: { id: string; kind?: string }) =>
    isMapDefinition(def)
      ? mapPageHref(currentLocale)
      : def.kind === "canvas"
        ? `/${currentLocale}/canvas/${def.id}`
        : `/${currentLocale}/design/${def.id}`;

  // Every surface that can put something in the cart has to show the cart.
  // This has now been forgotten twice — the canvas editor shipped without it,
  // then the art catalogue did — so the rule is written down rather than
  // implied: if a page has an add-to-cart button, its route belongs here.
  const showCart =
    isHome || isCalendarPage || isDesignPage || isCanvasPage || isCataloguePage || isMapPage;
  // Each language in its own name (endonym), whatever the page's locale: the
  // CZ header read "Češki" and Slovak "Slovenski" — Croatian/Slovene names.
  const languageMap = {
    sl: "Slovenščina",
    en: "English",
    hr: "Hrvatski",
    sk: "Slovenčina",
    it: "Italiano",
    "de-AT": "Deutsch",
    cs: "Čeština",
    pl: "Polski",
  };
  useEffect(() => {
    // Set the locale cookie whenever the currentLocale changes
    setLocaleFromStorefrontHost({ currentLocale });
    nookies.set(null, "locale", currentLocale || "sl", {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      path: "/",
      sameSite: "lax", // or 'strict' or 'none'
      secure: process.env.NODE_ENV === "production", // set to true in production
    });
  }, [currentLocale]);
  /**
   * First-visit locale redirect, keyed off the storefront's own domain.
   *
   * Was `setLocaleBasedOnReferer`, reading a `referer` prop that no call site
   * ever passed — so it always saw "https://fotodelavnica.si", matched nothing,
   * and never fired. Same defect as the home link above.
   *
   * Still guarded by the locale cookie, so it only ever acts on a customer's
   * first visit and never overrides a language they chose themselves.
   */
  const setLocaleFromStorefrontHost = ({
    currentLocale,
  }: {
    currentLocale: string | undefined;
  }) => {
    if (typeof window === "undefined") return;
    const cookies = nookies.get(null);
    if ("locale" in cookies) return;
    const hostLocale = storefrontLocaleForHost(window.location.hostname);
    const targetLocale =
      hostLocale && hostLocale !== currentLocale ? hostLocale : null;
    if (targetLocale) {
      const currentPath = window.location.pathname;
      // Always add locale prefix and strip existing locale first
      const pathWithoutLocale = currentPath.replace(/^\/(en|hr|sk|cs|de-AT|it|pl|sl)(\/|$)/, "/");
      const newPath = `/${targetLocale}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
      window.location.href = newPath + window.location.search;
    }
  };
  // The token for THIS order, including one the head guard moved out of the
  // URL so analytics could not read it — but only on a URL that names this
  // order, so the links carry it exactly where they did before the guard
  // (guest-token-url-guard.ts).
  const urlGuestToken =
    typeof window === "undefined"
      ? null
      : guestTokenForActiveOrder(orderContext?.id);
  const queryParamsToPass =
    orderContext?.status &&
    (orderContext?.status == OrderStatus.PENDING ||
      orderContext.status == OrderStatus.PROCESSING) &&
    orderContext?.id &&
    urlGuestToken
      ? {
          orderId: orderContext?.id ?? undefined,
          guest_session_token: urlGuestToken,
        }
      : {};
  return (
    <AppBar
      position={sticky ? "sticky" : "relative"}
      sx={(theme) => ({
        width: '100%',
        maxWidth: 'none',
        left: 0,
        right: 0,
        ml: 0,
        mr: 0,
        backgroundColor: chromeSurface(theme, '#109bc5'),
        color: chromeText(theme, 'white'),
        // Dark only: no elevation overlay lightening the teal-ink, and the
        // brand teal as the hover accent on the white-on-ink links.
        ...(theme.palette.mode === 'dark' && {
          backgroundImage: 'none',
          '& a:hover': { color: DARK_CHROME.accent },
        }),
      })}
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
          {/* Left side - Navigation links */}
          <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
            {/* Hamburger menu for mobile on non-admin pages */}
            {!isOnAdminPage && showHome && (
              <IconButton
                color="inherit"
                onClick={() => setNavDrawerOpen(true)}
                aria-label={translate("header.openMenu")}
                sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {/* Desktop navigation links */}
            {showHome && (
              <Stack direction="row" alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                {isHome && (
                  <MuiLink
                    href={homeLink}
                    ml="10px"
                    mr="10px"
                    variant="body1"
                    color="inherit"
                    underline="none"
                    fontSize="12px"
                    sx={{ cursor: "pointer" }}
                  >
                    {translate("header.parentPage")}
                  </MuiLink>
                )}
                <MuiLink
                  component={Link as React.ElementType}
                  href={{ pathname: REDIRECT_TO_HOME, query: queryParamsToPass }}
                  ml="10px"
                  mr="10px"
                  variant="body1"
                  color="inherit"
                  underline="none"
                  fontSize="12px"
                  sx={{ cursor: "pointer" }}
                >
                  {translate("header.home")}
                </MuiLink>
                {/* Produkti — hover-opens the dropdown; clicking the label
                    navigates to the Produkti page (no separate "all products"
                    entry needed). */}
                <Box
                  onMouseEnter={(e: React.MouseEvent<HTMLElement>) => openProdukti(e.currentTarget)}
                  onMouseLeave={scheduleCloseProdukti}
                  sx={{ display: "inline-flex", ml: "10px", mr: "10px" }}
                >
                  <Box
                    component={NextLink as React.ElementType}
                    href={`/${currentLocale}/produkti`}
                    aria-controls={produktiOpen ? "produkti-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={produktiOpen ? "true" : undefined}
                    onClick={() => setProduktiAnchorEl(null)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "2px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "inherit",
                      textDecoration: "none",
                      fontSize: "12px",
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      p: 0,
                    }}
                  >
                    {translate("header.products")}
                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
                <Menu
                  id="produkti-menu"
                  anchorEl={produktiAnchorEl}
                  open={produktiOpen}
                  onClose={() => setProduktiAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  hideBackdrop
                  disableRestoreFocus
                  // Let the pointer reach the trigger through the (invisible) root;
                  // re-enable pointer events on the list so the items stay hoverable.
                  sx={{ pointerEvents: "none" }}
                  slotProps={{
                    list: {
                      "aria-labelledby": "produkti-button",
                      onMouseEnter: () => {
                        if (produktiCloseTimer.current) clearTimeout(produktiCloseTimer.current);
                      },
                      onMouseLeave: scheduleCloseProdukti,
                      sx: { pointerEvents: "auto" },
                    },
                  }}
                >
                  <MenuItem
                    component={Link as React.ElementType}
                    href={{ pathname: REDIRECT_TO_HOME, query: queryParamsToPass }}
                    onClick={() => setProduktiAnchorEl(null)}
                    sx={{ fontSize: "13px" }}
                  >
                    {translate("produkti.photos")}
                  </MenuItem>
                  <MenuItem
                    component={Link as React.ElementType}
                    href={{ pathname: `/${currentLocale}/calendar`, query: queryParamsToPass }}
                    onClick={() => setProduktiAnchorEl(null)}
                    sx={{ fontSize: "13px" }}
                  >
                    {translate("produkti.calendars")}
                  </MenuItem>
                  {artCatalogueEnabled && (
                  <MenuItem
                    component={NextLink as React.ElementType}
                    href={`/${currentLocale}/wall-art`}
                    onClick={() => setProduktiAnchorEl(null)}
                    sx={{ fontSize: "13px" }}
                  >
                    {translate("produkti.wallArt")}
                  </MenuItem>
                  )}
                  {/* Canvas definitions are reached through the wall-art hub,
                      which offers the upload, the art library and photography
                      together. Listing them here as well would put the same
                      product in the menu twice under two different names. */}
                  {activeDefinitions
                    .filter(
                      (def) => def.kind !== "canvas" || !artCatalogueEnabled,
                    )
                    .map((def) => (
                    <MenuItem
                      key={def.id}
                      component={NextLink as React.ElementType}
                      href={definitionHref(def)}
                      onClick={() => setProduktiAnchorEl(null)}
                      sx={{ fontSize: "13px" }}
                    >
                      {getDefName(def)}
                    </MenuItem>
                  ))}
                  {bundlesOffered && (
                    <MenuItem
                      component={NextLink as React.ElementType}
                      href={`/${currentLocale}/paketi`}
                      onClick={() => setProduktiAnchorEl(null)}
                      sx={{ fontSize: "13px" }}
                    >
                      {translate("bundles.navLabel")}
                    </MenuItem>
                  )}
                  {giftCardsEnabled && (
                  <MenuItem
                    component={NextLink as React.ElementType}
                    href={`/${currentLocale}/darilni-bon`}
                    onClick={() => setProduktiAnchorEl(null)}
                    sx={{ fontSize: "13px" }}
                  >
                    {translate("giftCard.title")}
                  </MenuItem>
                  )}
                </Menu>
              </Stack>
            )}
          </Stack>
          {/* Right side - Actions */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="16px"
            color={"inherit"}
            sx={{ flexShrink: 0 }}
          >
            <FormControl sx={{ minWidth: 64 }}>
              <Select
                disableUnderline
                defaultValue={currentLocale}
                inputProps={{ "aria-label": "Without label" }}
                variant="standard"
                sx={{
                  color: "inherit",
                  "& .MuiSvgIcon-root": {
                    color: "inherit",
                  },
                  "& .MuiStack-root > .MuiTypography-root": {
                    display: {
                      xs: "none",
                      sm: "block",
                    },
                  },
                }}
              >
                {[...(locales ?? [])].sort().map((lang: string) => (
                  <MenuItem
                    onClick={() => {
                      if (lang !== currentLocale && typeof window !== "undefined") {
                        const currentPath = window.location.pathname;
                        const currentSearch = window.location.search;
                        // Always add locale prefix and strip existing locale first
                        const pathWithoutLocale = currentPath.replace(/^\/(en|hr|sk|cs|de-AT|it|pl|sl)(\/|$)/, "/");
                        const newPath = `/${lang}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
                        nookies.set(null, "locale", lang, { maxAge: 30 * 24 * 60 * 60, path: "/" });
                        window.location.href = newPath + currentSearch;
                      }
                    }}
                    selected={currentLocale === lang}
                    key={lang}
                    defaultValue={lang}
                    value={lang}
                    sx={
                      mode === "light"
                        ? {
                            bgcolor: "#33333366",
                            "&:hover": {
                              backgroundColor: "primary.main", // your hover color
                            },
                            "&.Mui-selected": {
                              backgroundColor: "primary.main", // your selected color
                              // if you want to also style the hover color for selected items
                              "&:hover": {
                                backgroundColor: "primary.main", // your selected hover color
                              },
                            },
                          }
                        : {}
                    }
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Avatar
                        sx={{
                          width: "24px",
                          height: "24px",
                          marginRight: "5px",
                        }}
                        src={`/images/flags/${lang}.svg`}
                        alt={languageMap[lang as keyof typeof languageMap]}
                        slotProps={{ img: { width: 24, height: 24, loading: "lazy" } }}
                      />
                      <Typography
                        ml="4px"
                        mr="4px"
                        color={"inherit"}
                        fontSize="12px"
                      >
                        {languageMap[lang as keyof typeof languageMap]}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              color="inherit"
              onClick={() => {
                setMode();
              }}
              aria-label={translate("header.toggleTheme")}
            >
              {mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton>
            {user?.email ? (
              <>
                <Stack
                  direction="row"
                  gap="16px"
                  alignItems="center"
                  justifyContent="center"
                  onClick={handleClickUserSettings}
                >
                  {user?.email && (
                    <Typography
                      sx={{
                        display: {
                          xs: "none",
                          sm: "inline-block",
                        },
                      }}
                      variant="body2"
                    >
                      {user?.email}
                    </Typography>
                  )}
                  <Avatar alt={user?.email} />
                </Stack>
                <Menu
                  anchorEl={anchorEl}
                  open={showUserSettings}
                  onClose={handleCloseUserMenu}
                  slotProps={{
                    list: { "aria-labelledby": "basic-button" },
                  }}
                >
                  {isAdmin && (
                    <MenuItem
                      ml="10px"
                      mr="10px"
                      variant="body2"
                      color="inherit"
                      component={NextLink as React.ElementType}
                      underline="none"
                      href={REDIRECT_TO_ADMIN}
                      fontSize="12px"
                    >
                      {translate("header.admin")}
                    </MenuItem>
                  )}
                  <MenuItem
                    ml="10px"
                    mr="10px"
                    variant="body1"
                    color="inherit"
                    component={Link as React.ElementType}
                    underline="none"
                    href={REDIRECT_TO_ORDER_HISTORY}
                    fontSize="12px"
                  >
                    {translate("header.myOrders")}
                  </MenuItem>
                  {partyEnabled && (
                    <MenuItem
                      ml="10px"
                      mr="10px"
                      variant="body1"
                      color="inherit"
                      component={Link as React.ElementType}
                      underline="none"
                      href={REDIRECT_TO_MY_EVENTS}
                      fontSize="12px"
                    >
                      {translate("header.myEvents")}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      authProvider.logout();
                      window.location.reload();
                    }}
                  >
                    {translate("header.logout")}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <MuiLink
                component={Link as React.ElementType}
                href={REDIRECT_TO_LOGIN}
                ml="4px"
                mr="4px"
                color={"inherit"}
                underline="none"
                fontSize="12px"
                sx={{ cursor: "pointer" }}
              >
                {translate("header.login")}
              </MuiLink>
            )}
            {showCart && (
              <IconButton
                color="inherit"
                onClick={handleOpenDrawer}
                aria-label={translate("header.cart")}
                sx={{
                  px: 0,
                }}
              >
                <Badge
                  badgeContent={getTotalItemsCount()}
                  color="error"
                  showZero={false}
                  sx={{
                    "& .MuiBadge-badge": {
                      height: "18px",
                      minWidth: "18px",
                      fontSize: "0.7rem",
                      padding: "0 4px",
                    },
                  }}
                >
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Toolbar>
      {/* Nothing at all unless a cashback akcija is running: header height is unchanged while the switch is off. */}
      <CashbackBanner />
      {/* Nothing at all unless this customer has credit; never on the checkout, admin or in the app. */}
      <CreditReminderRibbon />
      {/* Navigation Drawer for mobile */}
      <Drawer
        anchor="left"
        open={navDrawerOpen}
        onClose={() => setNavDrawerOpen(false)}
      >
        <Box sx={{ width: 250, pt: 2 }} role="presentation">
          <List>
            {isHome && (
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href={homeLink}
                  onClick={() => setNavDrawerOpen(false)}
                >
                  <ListItemText primary={translate("header.parentPage")} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton
                component={Link as React.ElementType}
                href={{
                  pathname: REDIRECT_TO_HOME,
                  query: queryParamsToPass,
                }}
                onClick={() => setNavDrawerOpen(false)}
              >
                <ListItemText primary={translate("header.home")} />
              </ListItemButton>
            </ListItem>
            {/* Produkti sub-items in mobile drawer — the section header links to
                the Produkti page (no separate "all products" entry). */}
            <ListItem disablePadding>
              <ListItemButton
                component={NextLink as React.ElementType}
                href={`/${currentLocale}/produkti`}
                onClick={() => setNavDrawerOpen(false)}
                sx={{ pl: 2, py: 0.5 }}
              >
                <ListItemText
                  primary={translate("header.products")}
                  slotProps={{ primary: { sx: { fontSize: "11px", fontWeight: 700, textTransform: "uppercase", opacity: 0.6 } } }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link as React.ElementType}
                href={{ pathname: REDIRECT_TO_HOME, query: queryParamsToPass }}
                onClick={() => setNavDrawerOpen(false)}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={translate("produkti.photos")} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link as React.ElementType}
                href={{ pathname: `/${currentLocale}/calendar`, query: queryParamsToPass }}
                onClick={() => setNavDrawerOpen(false)}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={translate("produkti.calendars")} />
              </ListItemButton>
            </ListItem>
            {activeDefinitions.map((def) => (
              <ListItem key={def.id} disablePadding>
                <ListItemButton
                  component={NextLink as React.ElementType}
                  href={definitionHref(def)}
                  onClick={() => setNavDrawerOpen(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={getDefName(def)} />
                </ListItemButton>
              </ListItem>
            ))}
            {bundlesOffered && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NextLink as React.ElementType}
                  href={`/${currentLocale}/paketi`}
                  onClick={() => setNavDrawerOpen(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={translate("bundles.navLabel")} />
                </ListItemButton>
              </ListItem>
            )}
            {giftCardsEnabled && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NextLink as React.ElementType}
                  href={`/${currentLocale}/darilni-bon`}
                  onClick={() => setNavDrawerOpen(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={translate("giftCard.title")} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
          <Divider />
          {user?.email ? (
            <List>
              {isAdmin && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={NextLink as React.ElementType}
                    href={REDIRECT_TO_ADMIN}
                    onClick={() => setNavDrawerOpen(false)}
                  >
                    <ListItemText primary={translate("header.admin")} />
                  </ListItemButton>
                </ListItem>
              )}
              <ListItem disablePadding>
                <ListItemButton
                  component={Link as React.ElementType}
                  href={REDIRECT_TO_ORDER_HISTORY}
                  onClick={() => setNavDrawerOpen(false)}
                >
                  <ListItemText primary={translate("header.myOrders")} />
                </ListItemButton>
              </ListItem>
              {partyEnabled && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link as React.ElementType}
                    href={REDIRECT_TO_MY_EVENTS}
                    onClick={() => setNavDrawerOpen(false)}
                  >
                    <ListItemText primary={translate("header.myEvents")} />
                  </ListItemButton>
                </ListItem>
              )}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    authProvider.logout();
                    window.location.reload();
                  }}
                >
                  <ListItemText primary={translate("header.logout")} />
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link as React.ElementType}
                  href={REDIRECT_TO_LOGIN}
                  onClick={() => setNavDrawerOpen(false)}
                >
                  <ListItemText primary={translate("header.login")} />
                </ListItemButton>
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>
      {/* Cart Drawer - rendered outside the toolbar to avoid layout issues */}
      {showCart && <CartDrawer />}
    </AppBar>
  );
};
